"use client";

import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Fuse from 'fuse.js';
import { BlogPost } from '@/types/blog';
import { cn } from '@/lib/utils';

export interface BlogSearchProps {
  posts: BlogPost[];
  className?: string;
  placeholder?: string;
  maxResults?: number;
  initialQuery?: string;
}

interface SearchResult {
  item: BlogPost;
  score?: number;
}

export function BlogSearch({
  posts,
  className,
  placeholder = "Search financial tips, guides, and insights...",
  maxResults = 8,
  initialQuery = ''
}: BlogSearchProps) {
  const router = useRouter();
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);
  const [suppressDropdown, setSuppressDropdown] = useState(Boolean(initialQuery)); // Don't show dropdown if page loaded with search

  // Clear search input when navigating back from search results (initialQuery becomes empty)
  useEffect(() => {
    if (initialQuery === '') {
      setQuery('');
      setIsOpen(false);
      setSuppressDropdown(false);
    } else {
      setQuery(initialQuery);
    }
  }, [initialQuery]);

  // Configure Fuse.js for optimal search experience
  const fuse = useMemo(() => {
    const options = {
      keys: [
        { name: 'metadata.title', weight: 3 },           // Title most important
        { name: 'metadata.description', weight: 2 },     // Description high priority  
        { name: 'metadata.tags', weight: 2 },            // Tags high priority
        { name: 'content', weight: 1 },                  // Content lower priority
        { name: 'metadata.category', weight: 1.5 },      // Category medium priority
        { name: 'metadata.seoKeywords', weight: 2 },     // SEO keywords high priority
      ],
      threshold: 0.4,        // Balance between fuzzy and exact matching
      distance: 100,         // How far to search for matches
      minMatchCharLength: 2, // Minimum characters to trigger search
      includeScore: true,    // Include relevance scores
    };
    
    return new Fuse(posts, options);
  }, [posts]);

  // Perform search when query changes
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    const searchResults = fuse.search(query, { limit: maxResults });
    setResults(searchResults);
    // Only show dropdown if not suppressed (i.e., not showing full search results on page)
    setIsOpen(searchResults.length > 0 && !suppressDropdown);
  }, [query, fuse, maxResults, suppressDropdown]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.blog-search-container')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getCategoryColor = (category: string) => {
    const colors = {
      'financial-tips': 'bg-blue-100 text-blue-800',
      'investment': 'bg-green-100 text-green-800',
      'savings': 'bg-purple-100 text-purple-800',
      'budgeting': 'bg-orange-100 text-orange-800',
      'company-news': 'bg-gray-100 text-gray-800',
      default: 'bg-gray-100 text-gray-800'
    };
    
    return colors[category as keyof typeof colors] || colors.default;
  };


  return (
    <div className={cn('blog-search-container relative max-w-2xl mx-auto', className)}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <svg 
            className="h-5 w-5 text-gray-400" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
            />
          </svg>
        </div>
        
        <input
          ref={setInputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSuppressDropdown(false); // Allow dropdown when user types
          }}
          onFocus={() => {
            if (query.length >= 2 && !suppressDropdown) {
              setIsOpen(true);
            }
          }}
          className="w-full pl-12 pr-4 py-4 text-gray-900 bg-white border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-transparent shadow-sm"
        />
        
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setIsOpen(false);
              setSuppressDropdown(false);
              router.push('/blog');
            }}
            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-y-auto z-50">
          <div className="p-3 border-b border-gray-100">
            <p className="text-sm text-gray-600">
              Found {results.length} result{results.length === 1 ? '' : 's'} for &ldquo;{query}&rdquo;
            </p>
          </div>
          
          <div className="py-2">
            {results.map((result) => {
              const post = result.item;
              const { metadata } = post;
              
              return (
                <Link
                  key={metadata.slug}
                  href={`/blog/${metadata.slug}`}
                  onClick={() => {
                    setIsOpen(false);
                    setQuery('');
                  }}
                  className="block px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {/* Post Info */}
                    <div className="flex-1 min-w-0">
                      {/* Category & Date */}
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={cn(
                            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                            getCategoryColor(metadata.category)
                          )}
                        >
                          {metadata.category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        
                        <span className="text-xs text-gray-500">
                          {new Date(metadata.publishDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="font-semibold text-gray-900 text-sm line-clamp-2 mb-1">
                        {metadata.title}
                      </h3>

                      {/* Description */}
                      <p className="text-xs text-gray-600 line-clamp-2">
                        {metadata.description}
                      </p>

                      {/* Meta */}
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span>By {metadata.author}</span>
                        <span>{metadata.readTime}</span>
                        {result.score && (
                          <span className="text-green-600">
                            {Math.round((1 - result.score) * 100)}% match
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          
          {/* View All Results Link */}
          <div className="p-3 border-t border-gray-100">
            <button
              onClick={() => {
                setIsOpen(false);
                setSuppressDropdown(true); // Suppress dropdown on the search results page
                inputRef?.blur();
                router.push(`/blog?search=${encodeURIComponent(query)}`);
              }}
              className="block w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              View all results for &ldquo;{query}&rdquo; →
            </button>
          </div>
        </div>
      )}

      {/* No Results */}
      {isOpen && query.length >= 2 && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
          <div className="p-6 text-center">
            <div className="text-gray-400 mb-2">
              <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.291-1.1-5.7-2.7" />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-1">No results found</h3>
            <p className="text-sm text-gray-500 mb-4">
              Try searching for &ldquo;savings&rdquo;, &ldquo;investment&rdquo;, or &ldquo;financial tips&rdquo;
            </p>
            <button
              onClick={() => {
                setIsOpen(false);
                setQuery('');
                router.push('/blog');
              }}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Browse all posts →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}